import { Select, Typography } from 'antd';
import moment, { Moment } from 'moment';
import React from 'react';
import {
  IBestDataset,
  IItemsValidation,
  IPlotVariable,
} from '../../../../../types/analytics';
import DatePicker from '../../../../../lib/MomentDatePicker';

import styles from '../../Style/ValidationPanel.module.scss';

const { Text } = Typography;

interface AltimeterComparisonFormProps {
  startDate: moment.Moment;
  endDate: moment.Moment;
  previewDatasetId: string;
  previewDatasetTimeRange?: [moment.Moment, moment.Moment];
  datasets: IBestDataset[];
  categories: string[];
  variablesX: IPlotVariable[];
  variableX: IPlotVariable | null;
  notSupported: boolean;
  datasetChange: (item: string) => void;
  doVariableChange: (active: number | string) => void;
  doStartDateChange: (value: Moment | null) => void;
  doEndDateChange: (value: Moment | null) => void;
  isUpdating: boolean;
}

export const AltimeterComparisonForm: React.FC<
  AltimeterComparisonFormProps
> = ({
  startDate,
  endDate,
  previewDatasetId,
  previewDatasetTimeRange,
  datasets,
  variablesX,
  variableX,
  notSupported,
  datasetChange,
  doVariableChange,
  doStartDateChange,
  doEndDateChange,
  isUpdating,
}) => {
  const getOptionValue = (
    item: IPlotVariable | IItemsValidation
  ): string | number => {
    if ('Index' in item) {
      return item.Index;
    } else {
      return item.name;
    }
  };

  const getOptionLabel = (item: IPlotVariable | IItemsValidation): string => {
    if ('Name' in item) {
      return `${item.Name}${item.Unit ? ` (${item.Unit})` : ''}`;
    } else {
      return `${item.details}${item.unit ? ` (${item.unit})` : ''}`;
    }
  };

  const disabledStartDate = (current: Moment) => {
    if (notSupported) return true;
    // Can not select date after end date
    if (endDate) {
      const isAfterEndDate = current && current > moment(endDate);
      // and not before preview dataset's time range and not after preview dataset's time range
      return isAfterEndDate
        ? isAfterEndDate
        : current.isBefore(previewDatasetTimeRange?.[0], 'day');
    }
    return true;
  };

  const disabledEndDate = (current: Moment) => {
    if (!startDate || notSupported) {
      return true;
    }

    const isBeforeStartDate = current.isBefore(startDate, 'day');
    const isAfterPreviewRange = current.isAfter(
      previewDatasetTimeRange?.[1],
      'day'
    );

    return isBeforeStartDate || isAfterPreviewRange;
  };

  return (
    <div className={styles.altimeterForm}>
      <div>
        <Text>Dataset</Text>
        <Select
          id='Altimeter-Comparison-Form_Dataset_Select'
          value={previewDatasetId}
          className={styles.altimeterFormSelect}
          onChange={datasetChange}
          placeholder='Select dataset'
          disabled={notSupported || isUpdating}
        >
          {datasets.map((item) => (
            <Select.Option key={item.id} value={item.id}>
              {item.value}
            </Select.Option>
          ))}
        </Select>
      </div>

      <div>
        <Text>Variable</Text>
        <Select
          id='Altimeter-Comparison-Form_Variable_Select'
          placeholder='Select variable'
          onChange={doVariableChange}
          options={variablesX.map((item) => ({
            value: getOptionValue(item),
            label: getOptionLabel(item),
          }))}
          className={styles.altimeterFormSelect}
          value={variableX?.Index}
          disabled={notSupported || isUpdating}
        />
      </div>

      <div>
        <Text>Start Date</Text>
        <DatePicker
          id='Altimeter-Comparison-Form_Start_Date'
          onChange={doStartDateChange}
          className={styles.altimeterFormDateSelect}
          placeholder='Select start date'
          disabled={notSupported || isUpdating}
          disabledDate={disabledStartDate}
          value={startDate ? moment(startDate) : null}
        />
      </div>

      <div>
        <Text>End Date</Text>
        <DatePicker
          id='Altimeter-Comparison-Form_End_Date'
          onChange={doEndDateChange}
          disabledDate={disabledEndDate}
          className={styles.altimeterFormDateSelect}
          placeholder='Select end date'
          disabled={notSupported || isUpdating}
          value={endDate ? moment(endDate) : null}
        />
      </div>
    </div>
  );
};
